import { useState } from "react";
import { Copy, CheckCircle, XCircle, AlertTriangle } from "lucide-react";
import { Badge } from "./Badge";
import { useSsoConfig, useSaveSsoConfig, useSetSsoStatus, useTestSsoConnection, useDeleteSsoConfig } from "@platform/hooks";
import { useToast } from "@nthamizh/ui";
import { cn } from "@nthamizh/ui";

interface SsoConfigCardProps {
  enterpriseId: string;
  enterpriseSlug: string;
  apiBaseUrl?: string;
}

const PROVIDERS = [
  { id: "google",    name: "Google Workspace", type: "OIDC",        issuerUrl: "https://accounts.google.com", icon: "🔵" },
  { id: "microsoft", name: "Microsoft Entra",  type: "OIDC / SAML", issuerUrl: "https://login.microsoftonline.com", icon: "🟦" },
  { id: "okta",      name: "Okta",             type: "OIDC / SAML", issuerUrl: "",  icon: "🔷" },
  { id: "ping",      name: "Ping Identity",    type: "SAML",        issuerUrl: "",  icon: "🟠" },
  { id: "adfs",      name: "ADFS",             type: "SAML",        issuerUrl: "",  icon: "🟩" },
  { id: "custom",    name: "Custom",           type: "SAML or OIDC",issuerUrl: "",  icon: "⚙️" },
] as const;

const OIDC_PROVIDERS = ["google", "microsoft", "okta", "custom"] as const;

interface SsoConfig {
  id: string; protocol: "oidc" | "saml"; providerName: string;
  clientId: string | null; domainHint: string | null;
  metadataXml: string | null; issuerUrl: string | null;
  status: "configured" | "active" | "disabled";
  testedAt: string | null; testPassed: boolean | null;
  hasSecret: boolean;
}

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div className="mb-3">
      <label className="block text-xs font-medium text-ds-text-secondary mb-1.5">{label}</label>
      <div className="flex items-center gap-2">
        <code className="flex-1 px-3 py-2 text-xs font-mono bg-ds-bg border border-ds-border rounded-lg text-ds-text-secondary truncate">{value}</code>
        <button onClick={copy} className="flex items-center gap-1 px-2.5 py-2 rounded-lg border border-ds-border bg-white hover:bg-ds-bg text-xs text-ds-text-secondary flex-shrink-0">
          <Copy className="w-3 h-3" /> {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}

export function SsoConfigCard({ enterpriseId, enterpriseSlug, apiBaseUrl = "" }: SsoConfigCardProps) {
  const { data: rawConfig, isLoading } = useSsoConfig(enterpriseId);
  const saveConfig = useSaveSsoConfig();
  const setStatus = useSetSsoStatus();
  const testConn = useTestSsoConnection();
  const deleteConfig = useDeleteSsoConfig();
  const { toast } = useToast();

  const config = rawConfig as SsoConfig | null | undefined;

  const [editing, setEditing] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState("google");
  const [protocol, setProtocol] = useState<"oidc" | "saml">("oidc");
  const [form, setForm] = useState({ clientId: "", clientSecret: "", domainHint: "", metadataXml: "", issuerUrl: "" });
  const [testResult, setTestResult] = useState<{ passed: boolean; detail: string } | null>(null);
  const [showDelete, setShowDelete] = useState(false);

  const oidcCb = `${apiBaseUrl}/v1/auth/sso/${enterpriseSlug}/oidc/callback`;
  const samlAcs = `${apiBaseUrl}/v1/auth/sso/${enterpriseSlug}/saml/callback`;
  const samlEntity = `${apiBaseUrl}/sso/saml/${enterpriseSlug}/metadata`;

  const startEdit = () => {
    if (config) {
      const prov = PROVIDERS.find((p) => p.name === config.providerName) ?? PROVIDERS[0]!;
      setSelectedProvider(prov.id);
      setProtocol(config.protocol);
      setForm({
        clientId: config.clientId ?? "",
        clientSecret: "",
        domainHint: config.domainHint ?? "",
        metadataXml: config.metadataXml ?? "",
        issuerUrl: config.issuerUrl ?? "",
      });
    }
    setEditing(true);
    setTestResult(null);
  };

  const handleProviderSelect = (id: string) => {
    setSelectedProvider(id);
    const prov = PROVIDERS.find((p) => p.id === id)!;
    const isOidc = OIDC_PROVIDERS.includes(id as typeof OIDC_PROVIDERS[number]);
    setProtocol(id === "custom" ? "oidc" : isOidc && !prov.type.includes("SAML") ? "oidc" : prov.type === "SAML" ? "saml" : "oidc");
    setForm((f) => ({ ...f, issuerUrl: prov.issuerUrl }));
  };

  const handleSave = async () => {
    const prov = PROVIDERS.find((p) => p.id === selectedProvider)!;
    try {
      await saveConfig.mutateAsync({
        enterpriseId, protocol, providerName: prov.name,
        ...(protocol === "oidc" ? {
          clientId: form.clientId, clientSecret: form.clientSecret || undefined,
          domainHint: form.domainHint || undefined, issuerUrl: form.issuerUrl || undefined,
        } : {
          metadataXml: form.metadataXml, domainHint: form.domainHint || undefined,
        }),
      });
      toast({ title: "SSO configuration saved", variant: "success" });
      setEditing(false);
    } catch { toast({ title: "Failed to save SSO config", variant: "error" }); }
  };

  const handleTest = async () => {
    try {
      const r = await testConn.mutateAsync(enterpriseId);
      setTestResult(r);
      toast({ title: r.passed ? "Connection test passed" : "Connection test failed", variant: r.passed ? "success" : "error" });
    } catch { toast({ title: "Test failed", variant: "error" }); }
  };

  const handleEnable = async () => {
    try { await setStatus.mutateAsync({ enterpriseId, status: "active" }); toast({ title: "SSO enabled", variant: "success" }); }
    catch { toast({ title: "Failed to enable SSO", variant: "error" }); }
  };

  const handleDisable = async () => {
    try { await setStatus.mutateAsync({ enterpriseId, status: "disabled" }); toast({ title: "SSO disabled", variant: "success" }); }
    catch { toast({ title: "Failed to disable SSO", variant: "error" }); }
  };

  const handleDelete = async () => {
    try { await deleteConfig.mutateAsync(enterpriseId); toast({ title: "SSO configuration removed", variant: "success" }); setShowDelete(false); }
    catch { toast({ title: "Failed to delete SSO config", variant: "error" }); }
  };

  const statusBadge = !config
    ? <Badge variant="gray">Not configured</Badge>
    : config.status === "active" ? <Badge variant="green">Active</Badge>
    : config.status === "disabled" ? <Badge variant="red">Disabled</Badge>
    : <Badge variant="amber">Configured — not enabled</Badge>;

  if (isLoading) return null;

  return (
    <div className="bg-white border border-ds-border rounded-card shadow-card p-5 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <p className="text-xs font-semibold text-ds-text-muted uppercase tracking-wide">Single sign-on</p>
            {statusBadge}
          </div>
          <p className="text-xs text-ds-text-secondary">
            {config?.status === "active"
              ? `Users on ${config.domainHint ? `@${config.domainHint}` : "your domain"} sign in via ${config.providerName}.`
              : "Let your team sign in with their corporate identity provider."}
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {!config && !editing && (
            <button onClick={startEdit} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700">
              Set up SSO
            </button>
          )}
          {config && !editing && (
            <>
              {config.status === "active"
                ? <button onClick={handleDisable} disabled={setStatus.isPending} className="px-3 py-1.5 rounded-lg bg-red-50 text-red-600 border border-red-200 text-sm font-medium hover:bg-red-100 disabled:opacity-50">Disable</button>
                : <button onClick={handleEnable} disabled={setStatus.isPending} className="px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 text-sm font-medium hover:bg-emerald-100 disabled:opacity-50">Enable</button>}
              <button onClick={startEdit} className="px-3 py-1.5 rounded-lg bg-white border border-ds-border text-sm text-ds-text-secondary hover:bg-ds-bg">Edit</button>
            </>
          )}
        </div>
      </div>

      {/* Active config summary */}
      {config && !editing && (
        <div className="space-y-2">
          {[
            { label: "Provider", value: config.providerName },
            { label: "Protocol", value: config.protocol.toUpperCase() },
            { label: "Domain restriction", value: config.domainHint ? `@${config.domainHint}` : "All users" },
            { label: "Last tested", value: config.testedAt ? `${new Date(config.testedAt).toLocaleString()} · ${config.testPassed ? "✓ Pass" : "✗ Fail"}` : "Not tested" },
          ].map(({ label, value }) => (
            <div key={label} className="flex items-center justify-between py-2 border-b border-ds-border last:border-0">
              <span className="text-xs text-ds-text-secondary">{label}</span>
              <span className="text-xs font-medium text-ds-text">{value}</span>
            </div>
          ))}
          <div className="flex items-center justify-between py-2">
            <span className="text-xs text-ds-text-secondary">Test connection</span>
            <button onClick={handleTest} disabled={testConn.isPending}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 text-xs font-medium hover:bg-indigo-100 disabled:opacity-50">
              {testConn.isPending ? "Testing…" : "Run test"}
            </button>
          </div>
          {testResult && (
            <div className={cn("flex items-center gap-2 p-3 rounded-lg text-xs", testResult.passed ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600")}>
              {testResult.passed ? <CheckCircle className="w-4 h-4 flex-shrink-0" /> : <XCircle className="w-4 h-4 flex-shrink-0" />}
              {testResult.detail}
            </div>
          )}

          {/* Danger zone */}
          <div className="pt-3 border-t border-ds-border">
            <button onClick={() => setShowDelete(true)} className="text-xs text-red-500 hover:text-red-600">Remove SSO configuration</button>
            {showDelete && (
              <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200 space-y-2">
                <p className="text-xs text-red-700">This removes the SSO config. Users will fall back to email + password.</p>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setShowDelete(false)} className="px-2.5 py-1.5 rounded bg-white border border-ds-border text-xs text-ds-text-secondary">Cancel</button>
                  <button onClick={handleDelete} disabled={deleteConfig.isPending} className="px-2.5 py-1.5 rounded bg-red-600 text-white text-xs font-medium disabled:opacity-50">Confirm remove</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit / setup form */}
      {editing && (
        <div className="space-y-4">
          {/* Provider picker */}
          <div>
            <p className="text-xs font-medium text-ds-text-secondary mb-2">Identity provider</p>
            <div className="grid grid-cols-3 gap-2">
              {PROVIDERS.map((p) => (
                <button key={p.id} onClick={() => handleProviderSelect(p.id)}
                  className={cn("flex items-center gap-2 px-3 py-2.5 rounded-lg border text-left transition-colors",
                    selectedProvider === p.id ? "border-indigo-400 bg-indigo-50" : "border-ds-border hover:bg-ds-bg")}>
                  <span className="text-base">{p.icon}</span>
                  <div>
                    <p className="text-xs font-medium text-ds-text leading-tight">{p.name}</p>
                    <p className="text-[10px] text-ds-text-muted">{p.type}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Protocol toggle for providers that support both */}
          {["microsoft", "okta", "custom"].includes(selectedProvider) && (
            <div className="flex gap-2">
              {(["oidc", "saml"] as const).map((p) => (
                <button key={p} onClick={() => setProtocol(p)}
                  className={cn("flex-1 py-2 rounded-lg border text-xs font-medium transition-colors uppercase",
                    protocol === p ? "border-indigo-400 bg-indigo-50 text-indigo-700" : "border-ds-border text-ds-text-secondary hover:bg-ds-bg")}>
                  {p}
                </button>
              ))}
            </div>
          )}

          {/* OIDC fields */}
          {protocol === "oidc" && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-ds-text-secondary mb-1.5">Client ID</label>
                  <input value={form.clientId} onChange={(e) => setForm((f) => ({ ...f, clientId: e.target.value }))}
                    placeholder="From your identity provider"
                    className="w-full px-3 py-2 text-sm border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-ds-text-secondary mb-1.5">
                    Client secret {config?.hasSecret && <span className="text-emerald-600">(set — leave blank to keep)</span>}
                  </label>
                  <input type="password" value={form.clientSecret}
                    onChange={(e) => setForm((f) => ({ ...f, clientSecret: e.target.value }))}
                    placeholder={config?.hasSecret ? "Leave blank to keep existing" : "From your identity provider"}
                    className="w-full px-3 py-2 text-sm border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400" />
                </div>
              </div>
              {selectedProvider !== "google" && (
                <div>
                  <label className="block text-xs font-medium text-ds-text-secondary mb-1.5">Issuer URL</label>
                  <input value={form.issuerUrl} onChange={(e) => setForm((f) => ({ ...f, issuerUrl: e.target.value }))}
                    placeholder="https://your-idp.com"
                    className="w-full px-3 py-2 text-sm border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400" />
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-ds-text-secondary mb-1.5">Domain restriction (optional)</label>
                <div className="flex items-center border border-ds-border rounded-lg overflow-hidden">
                  <span className="px-2.5 text-sm text-ds-text-muted">@</span>
                  <input value={form.domainHint} onChange={(e) => setForm((f) => ({ ...f, domainHint: e.target.value }))}
                    placeholder="acme.com"
                    className="flex-1 py-2 pr-3 text-sm focus:outline-none" />
                </div>
                <p className="text-xs text-ds-text-muted mt-1">Only users with this email domain will be redirected to SSO.</p>
              </div>
              <CopyField label="Redirect URI — add this to your identity provider" value={oidcCb} />
            </div>
          )}

          {/* SAML fields */}
          {protocol === "saml" && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-ds-text-secondary mb-1.5">IdP metadata XML</label>
                <textarea value={form.metadataXml} onChange={(e) => setForm((f) => ({ ...f, metadataXml: e.target.value }))}
                  placeholder="Paste the XML from your identity provider's metadata download"
                  rows={5}
                  className="w-full px-3 py-2 text-xs font-mono border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400 resize-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-ds-text-secondary mb-1.5">Domain restriction (optional)</label>
                <div className="flex items-center border border-ds-border rounded-lg overflow-hidden">
                  <span className="px-2.5 text-sm text-ds-text-muted">@</span>
                  <input value={form.domainHint} onChange={(e) => setForm((f) => ({ ...f, domainHint: e.target.value }))}
                    placeholder="acme.com"
                    className="flex-1 py-2 pr-3 text-sm focus:outline-none" />
                </div>
              </div>
              <div className="space-y-2">
                <CopyField label="ACS URL (Single sign-on URL) — paste into your IdP" value={samlAcs} />
                <CopyField label="SP Entity ID (Audience URI) — paste into your IdP" value={samlEntity} />
              </div>
            </div>
          )}

          {/* Warning */}
          <div className="flex items-start gap-2.5 p-3 rounded-lg bg-amber-50 border border-amber-200">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">
              Once enabled, users with the configured domain will be redirected to your identity provider. Platform admins are never affected by enterprise SSO.
            </p>
          </div>

          <div className="flex gap-2 justify-end pt-1">
            <button onClick={() => setEditing(false)} className="px-3.5 py-2 rounded-lg bg-white border border-ds-border text-sm text-ds-text-secondary hover:bg-ds-bg">Cancel</button>
            <button onClick={handleSave} disabled={saveConfig.isPending}
              className="px-3.5 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
              {saveConfig.isPending ? "Saving…" : "Save configuration"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
